'use client'

import { ButtonArrow } from '@/components/ui/button-arrow'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { motion } from 'framer-motion'
import { ChevronRight } from 'lucide-react'
import Image from 'next/image'
import React, { useState } from 'react'
import { SiteHeadingV2 } from '../../fragments/site-heading-v2'
import { MessageDescriptor } from '@lingui/core'

interface ServiceSolution {
  title: MessageDescriptor
  description_top: MessageDescriptor
  description_bottom?: MessageDescriptor
  image: string
}

interface ServicesSolutionsProps {
  item: ServiceSolution
}

export function ServicesSolutions({ item }: ServicesSolutionsProps): React.JSX.Element {
  const { i18n } = useLingui()

  const { title, description_top, description_bottom, image } = item
  return (
    <div className='flex items-center justify-between gap-6 sm:gap-5 max-md:flex-col'>
      <motion.div
        className='flex flex-col gap-3 sm:gap-6 sm:w-full md:w-1/2 lg:w-2/5'
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        key={i18n._(item?.title)}
      >
        <h2 className='text-3xl font-semibold sm:text-4xl'>{i18n._(title)}</h2>
        <p className='font-light'>{i18n._(description_top)}</p>
        {description_bottom && <p className='font-light'>{i18n._(description_bottom)}</p>}

        {/* <ButtonArrow showSvg className='relative mt-6 max-sm:hidden'>
          {i18n._(`Read more`)}
        </ButtonArrow> */}
      </motion.div>
      <div className='relative sm:w-full md:w-1/2 lg:w-3/5'>
        <Image
          src={image}
          alt={i18n._(title)}
          width={720}
          height={360}
          className='object-contain ml-auto'
        />
      </div>
      <ButtonArrow showSvg className='relative hidden max-sm:flex max-sm:mr-auto'>
        {i18n._(`Read more`)}
      </ButtonArrow>
    </div>
  )
}

export default function ServicesSection() {
  const { i18n } = useLingui()
  const sampleData: ServiceSolution[] = [
    {
      title: msg`IoT, AI & Blockchain`,
      description_top: msg`We develop end-to-end IoT connectivity and bring AI into real-world applications, while leveraging blockchain for identity verification, traceability, and transparent transactions. Our goal is to transform real-world data into secure, practical solutions in agriculture, healthcare, real estate, and digital assets.`,
      image: '/landing/services-solutions/iot-ai.png'
    },
    {
      title: msg`Applied Technology Solutions`,
      description_top: msg`Building advanced technology platforms and applications across sectors such as digital assets, healthcare, agriculture, real estate, and finance, providing effective digital transformation solutions.`,
      image: '/landing/services-solutions/application-development.png'
    },
    {
      title: msg`Digital Platforms & Web 3.0`,
      description_top: msg`Developing Web3 systems, digital portals, e-commerce platforms, and integrated management solutions powered by blockchain technology, ensuring high security and intelligent user interaction.`,
      image: '/landing/services-solutions/game-development.png'
    },
    // {
    //   title: msg`Digital Transformation`,
    //   description_top: msg`We design and implement domain specific digital transformation, leveraging Blockchain, AI, and IoT to optimize operations, increase data transparency, and strengthen governance across finance, agriculture, healthcare, and more.`,
    //   image: '/landing/services-solutions/digital-transformation.png'
    // },
    {
      title: msg`Comprehensive Digital Transformation`,
      description_top: msg`Designing and implementing tailored digital transformation strategies for specific industries, applying Blockchain, AI, and IoT to optimize operations, ensure data transparency, and enhance management capacity.`,
      image: '/landing/services-solutions/uxui.png'
    },
    {
      title: msg`Digital Experience Design (UI/UX)`,
      description_top: msg`Creating user interfaces and optimized digital experiences to improve product usability, supporting platforms powered by Blockchain and AI.`,
      image: '/landing/services-solutions/website-development.png'
    },
    {
      title: msg`Metaverse & Virtual Environments`,
      description_top: msg`Developing immersive digital spaces for digital transformation, virtual reality, and next-generation customer experiences.`,
      image: '/landing/services-solutions/website-development.png'
    },
    {
      title: msg`Biotechnology & Genetic Encryption`,
      description_top: msg`Integrating Blockchain and AI to enable genetic traceability of crops and intellectual property protection for genetic data, with applications in agriculture.`,
      image: '/landing/services-solutions/website-development.png'
    },
    {
      title: msg`Quantum Computing (Future Vision)`,
      description_top: msg`Researching applications in system simulations, data optimization, and next-level cybersecurity.`,
      image: '/landing/services-solutions/website-development.png'
    },
    {
      title: msg`Magnetic Technology & New Energy (Long-term Research Vision)`,
      description_top: msg`Exploring sustainable energy solutions to support technical infrastructure and IoT-based sensor systems.`,
      image: '/landing/services-solutions/website-development.png'
    }
  ]

  const [selectedIndex, setSelectedIndex] = useState(0)

  return (
    <section className="padding-section min-h-svh h-full flex items-center bg-[url('/landing/home/services-solutions.png')] bg-cover bg-no-repeat bg-center">
      <div className='container'>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <SiteHeadingV2
            title={msg`Services & Solutions`}
            description={msg`PIONE GLOBAL is a technology corporation specializing in digital transformation services, pioneering the application of Blockchain, Artificial Intelligence (AI), and the Internet of Things (IoT) across multiple sectors such as digital assets, tokenized securities, asset management, agriculture, real estate, and healthcare. We deliver advanced technology solutions with practical applications for real life.`}
            classNameWrapper='max-md:hidden max-lg:pb-10'
          />
        </motion.div>
        <ServicesSolutions item={sampleData[selectedIndex]} />
        <motion.div
          className='grid h-full grid-cols-1 gap-5 max-sm:overflow-y-scroll sm:grid-cols-3 mt-7 sm:gap-7 sm:mt-14 lg:mt-10'
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          {sampleData.map((item, index) => (
            <button
              key={index}
              className={`p-3 px-4 sm:p-4 cursor-pointer lg:p-5 text-sm flex text-nowrap items-center justify-between !rounded-lg sm:!rounded-xl font-semibold ${selectedIndex === index ? 'bg-[#15B0D680]' : 'bg-[#15B0D633]'} hover:bg-[#15B0D680] transition`}
              onClick={() => setSelectedIndex(index)}
            >
              {i18n._(item.title)}
              <ChevronRight className='max-md:size-5' />
            </button>
          ))}
        </motion.div>
      </div>
    </section>
  )
}
